import { Observable } from 'rxjs';

import upp from 'app/app.module';
import * as storage from 'shared/childupp-local-storage';
import { determineEngine, determinePlatform, Engine, Platform } from 'shared/engine-dynamic';

class InstallationPromptService {

    /**
     * @private
     * @type {BeforeInstallPromptEvent}
     * @see https://developer.mozilla.org/en-US/docs/Web/API/BeforeInstallPromptEvent
     */
    _deferredPrompt = null;

    /**
     * Emits the 'beforeinstallprompt' event after it has been fired by the navigator.
     * Right on subscription if the event has not been emitted yet, null will be
     * emitted instead.
     *
     * @private
     * @type {Observable<BeforeInstallPromptEvent|null>}
     */
    _pwaInstallPrompt$ = new Observable((sub) => {
        if (this._deferredPrompt) {
            sub.next(this._deferredPrompt);
            sub.complete();
            return () => {};
        }

        const listener = (ev) => {
            ev.preventDefault();
            this._deferredPrompt = ev;
            sub.next(ev);
            sub.complete();
            return false;
        };
        window.addEventListener('beforeinstallprompt', listener);
        return () => {
            window.removeEventListener('beforeinstallprompt', listener);
        }
    });

    /**
     * Emits a boolean that determines if we have been able to capture the
     * 'beforeinstallprompt' event successfully.
     * @private
     * @type {Observable<boolean>}
     */
    _hasCapturedInstallationPrompt$ = new Observable((sub) => {
        const engine = determineEngine();
        const platform = determinePlatform();
        if (engine === Engine.BROWSER && platform === Platform.ANDROID) {
            // If running on an android browser wait for the actual event
            sub.next(false);
            return this._pwaInstallPrompt$.subscribe(
                (ev) => sub.next(!!ev),
                () => sub.next(false),
                () => sub.complete(),
            );
        } else {
            // Otherwise say we have captured the event (eventho it will
            // never actually happen)
            sub.next(true);
            sub.complete();
            return () => {};
        }
    });

    /**
     * Emits a boolean that determines if the current application session is
     * running after being launched via homescreen icon (i.e.: Typically a pwa)
     * @private
     * @type {Observable<boolean>}
     */
    _isAddedToHomescreen$ = new Observable((sub) => {
        const engine = determineEngine();
        const platform = determinePlatform();
        if (engine === Engine.BROWSER && platform === Platform.ANDROID) {
            sub.next(window.matchMedia('(display-mode: standalone)').matches);
        } else if (engine === Engine.BROWSER && platform === Platform.IOS) {
            sub.next(!!window.navigator.standalone);
        } else {
            sub.next(true);
        }
        sub.complete();
        return () => {}
    });

    /**
     * Emits a boolean that determines if enough time has passed since we last
     * prompted the user for pwa installation.
     * @private
     * @type {Observable<boolean>}
     */
    _hasPassedEnoughTimeSinceLastPrompt$ = new Observable((sub) => {
        storage.get(STORAGE_KEY_PROMPTED_AT)
        .then((lastPromptDate) => {
            const nextPromptDate = lastPromptDate + PROMPT_ONCE_EVERY;
            if (
                lastPromptDate === null // User was never prompted
                ||
                Date.now() >= nextPromptDate // Was prompted enough time ago
            ) {
                return true;
            } else {
                return false;
            }
        })
        .catch((e) => {
            return false;
        })
        .then((result) => {
            sub.next(result);
            sub.complete();
        });
    });

    static $inject = [ '$rootScope', '$q' ];

    constructor($rootScope, $q) {
        Object.defineProperties(this, {
            _$rootScope: { value: $rootScope },
            _$q: { value: $q },
        })
    }

    /**
     * Emits a boolean that determines if we should show the program-controlled
     * installation prompt.
     *
     * This prompt IS NOT the browser installation prompt, said prompt can be shown
     * later with {@link #showBrowserInstallationPrompt} but only AFTER this observable
     * has emitted a `true` value.
     *
     * @public
     * @type {Observable<boolean>}
     */
    shouldShowPrompt$ = Observable.combineLatest(
        this._isAddedToHomescreen$,
        this._hasPassedEnoughTimeSinceLastPrompt$,
        this._hasCapturedInstallationPrompt$,
        (isOnHomescreen, hasPassedEnoughTime, hasCapturedPrompt) => {
            return !isOnHomescreen && hasPassedEnoughTime && hasCapturedPrompt;
        }
    ).do(() => this._$rootScope.$evalAsync());


    /**
     * Tells the service that a program-controller prompt has been shown to
     * the user, allowing the service to handle the time-repetition window
     * until a prompt can be shown again.
     * @return {[type]} [description]
     */
    promptHasBeenShown() {
        return storage.save(STORAGE_KEY_PROMPTED_AT, Date.now());
    }

    /**
     * Shows the browser pwa installation prompt and returns a promise that
     * resolves after the user has accepted to install or gets rejected if
     * the user rejects the installation.
     *
     * If the {@link shouldShowPrompt$} has not emitted a true value yet, this
     * method will get rejected no matter what.
     *
     * @public
     * @return {Promise<void>}
     */
    showBrowserInstallationPrompt() {
        const engine = determineEngine();
        const platform = determinePlatform();
        if (engine === Engine.BROWSER && platform === Platform.ANDROID) {
            if (!this._deferredPrompt) {
                const msg = 'The pwa installation prompt was not captured, make sure you are calling #howBrowserInstallationPrompt AFTER shouldShowPrompt$ has emmited a true value';
                console.error(msg);
                return this._$q.reject(new Error(msg));
            }
            this._deferredPrompt.prompt()
            return this._$q.when(this._deferredPrompt.userChoice);
        } else {
            return this._$q.when();
        }
    }
}

/**
 * Storage key for a timestamp (Date.now(), milliseconds) of the last time
 * the user was shown an installation prompt.
 * @type {string}
 */
const STORAGE_KEY_PROMPTED_AT = 'prompted_for_installation_at';

/**
 * How many milliseconds have to pass between the user opening the app for it
 * to be prompted to install it again (if rejected the previous time).
 * @type {number}
 */
const PROMPT_ONCE_EVERY = 24 * 60 * 60 * 1000;


upp.service('installationPrompt', InstallationPromptService);



// WEBPACK FOOTER //
// ./src/app/common/components/installation-prompt/installation-prompt.service.js